import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardContent } from '../../../components/ui/Card';
import PieChart from '../../../components/analytics/PieChart';
import StackedBarChart from '../../../components/analytics/StackedBarChart';

interface UTMCampaign {
  id: string;
  name: string;
  source: string;
  medium: string;
  campaign: string;
  content?: string;
  term?: string;
  url: string;
  clicks: number;
  signups: number;
  conversions: number;
  revenue: number;
  createdAt: string;
}

interface UTMStats {
  topSources: { label: string; value: number; color: string }[];
  topMediums: { label: string; value: number; color: string }[];
  campaignPerformance: {
    label: string;
    segments: { name: string; value: number; color: string }[];
  }[];
}

/**
 * UTM Tracking Manager
 * Create trackable URLs and analyze marketing performance
 */
const UTMTrackingPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<UTMCampaign[]>([]);
  const [stats, setStats] = useState<UTMStats | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaign, setNewCampaign] = useState<Partial<UTMCampaign>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [baseUrl, setBaseUrl] = useState('https://yoursite.com');

  // Mock data for demo
  useEffect(() => {
    const mockCampaigns: UTMCampaign[] = [
      {
        id: '1',
        name: 'Twitter Launch Promo',
        source: 'twitter',
        medium: 'social',
        campaign: 'launch_2024',
        url: 'https://yoursite.com?utm_source=twitter&utm_medium=social&utm_campaign=launch_2024',
        clicks: 450,
        signups: 87,
        conversions: 23,
        revenue: 1150,
        createdAt: '2024-01-15',
      },
      {
        id: '2',
        name: 'Email Newsletter',
        source: 'newsletter',
        medium: 'email',
        campaign: 'weekly_update',
        content: 'banner',
        url: 'https://yoursite.com?utm_source=newsletter&utm_medium=email&utm_campaign=weekly_update&utm_content=banner',
        clicks: 320,
        signups: 95,
        conversions: 41,
        revenue: 2050,
        createdAt: '2024-02-01',
      },
      {
        id: '3',
        name: 'Facebook Ads',
        source: 'facebook',
        medium: 'cpc',
        campaign: 'tier_promo',
        term: 'patreon',
        url: 'https://yoursite.com?utm_source=facebook&utm_medium=cpc&utm_campaign=tier_promo&utm_term=patreon',
        clicks: 890,
        signups: 156,
        conversions: 67,
        revenue: 3350,
        createdAt: '2024-02-15',
      },
    ];
    setCampaigns(mockCampaigns);

    // Calculate stats
    const mockStats: UTMStats = {
      topSources: [
        { label: 'Facebook', value: 890, color: '#1877f2' },
        { label: 'Twitter', value: 450, color: '#1da1f2' },
        { label: 'Newsletter', value: 320, color: '#10b981' },
      ],
      topMediums: [
        { label: 'CPC (Paid)', value: 890, color: '#f59e0b' },
        { label: 'Social (Organic)', value: 450, color: '#3b82f6' },
        { label: 'Email', value: 320, color: '#10b981' },
      ],
      campaignPerformance: [
        {
          label: 'Tier Promo',
          segments: [
            { name: 'Clicks', value: 890, color: '#93c5fd' },
            { name: 'Signups', value: 156, color: '#60a5fa' },
            { name: 'Conversions', value: 67, color: '#3b82f6' },
          ],
        },
        {
          label: 'Weekly Update',
          segments: [
            { name: 'Clicks', value: 320, color: '#86efac' },
            { name: 'Signups', value: 95, color: '#4ade80' },
            { name: 'Conversions', value: 41, color: '#22c55e' },
          ],
        },
        {
          label: 'Launch 2024',
          segments: [
            { name: 'Clicks', value: 450, color: '#fcd34d' },
            { name: 'Signups', value: 87, color: '#fbbf24' },
            { name: 'Conversions', value: 23, color: '#f59e0b' },
          ],
        },
      ],
    };
    setStats(mockStats);
  }, []);

  const buildUTMUrl = (params: Partial<UTMCampaign>): string => {
    const url = new URL(baseUrl);
    if (params.source) url.searchParams.set('utm_source', params.source);
    if (params.medium) url.searchParams.set('utm_medium', params.medium);
    if (params.campaign) url.searchParams.set('utm_campaign', params.campaign);
    if (params.content) url.searchParams.set('utm_content', params.content);
    if (params.term) url.searchParams.set('utm_term', params.term);
    return url.toString();
  };

  const handleCreateCampaign = () => {
    if (!newCampaign.name || !newCampaign.source || !newCampaign.medium || !newCampaign.campaign) {
      alert('Please fill in all required fields');
      return;
    }

    const campaign: UTMCampaign = {
      id: Date.now().toString(),
      name: newCampaign.name,
      source: newCampaign.source,
      medium: newCampaign.medium,
      campaign: newCampaign.campaign,
      content: newCampaign.content,
      term: newCampaign.term,
      url: buildUTMUrl(newCampaign),
      clicks: 0,
      signups: 0,
      conversions: 0,
      revenue: 0,
      createdAt: new Date().toISOString().split('T')[0],
    };

    setCampaigns([...campaigns, campaign]);
    setShowCreateModal(false);
    setNewCampaign({});
  };

  const handleDeleteCampaign = (id: string) => {
    if (confirm('Are you sure you want to delete this campaign link?')) {
      setCampaigns(campaigns.filter((c) => c.id !== id));
    }
  };

  const handleCopyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);
  const totalSignups = campaigns.reduce((sum, c) => sum + c.signups, 0);
  const totalConversions = campaigns.reduce((sum, c) => sum + c.conversions, 0);
  const totalRevenue = campaigns.reduce((sum, c) => sum + c.revenue, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">UTM Tracking</h1>
          <p className="text-gray-600 mt-1">Track your marketing campaigns with UTM parameters</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Campaign Link
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {totalClicks.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <LinkIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Signups</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {totalSignups.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <FunnelIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {totalConversions.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <CheckIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ${totalRevenue.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Traffic by Source</h3>
            </CardHeader>
            <CardContent>
              <PieChart data={stats.topSources} size={300} innerRadius={0.5} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Traffic by Medium</h3>
            </CardHeader>
            <CardContent>
              <PieChart data={stats.topMediums} size={300} innerRadius={0.5} />
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Campaign Performance</h3>
            </CardHeader>
            <CardContent>
              <StackedBarChart data={stats.campaignPerformance} height={300} showValues={false} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900">Campaign Links</h3>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <div className="text-center py-12">
              <LinkIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaign links yet</h3>
              <p className="text-gray-600 mb-4">
                Create your first UTM-tracked link to start measuring marketing performance
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600"
              >
                <PlusIcon className="w-5 h-5" />
                Create First Link
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Campaign</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Source</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Medium</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Clicks</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Signups</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Conv</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Revenue</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const conversionRate =
                      campaign.clicks > 0
                        ? ((campaign.conversions / campaign.clicks) * 100).toFixed(1)
                        : '0';
                    return (
                      <tr key={campaign.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">{campaign.name}</div>
                            <div className="text-xs text-gray-500 truncate max-w-md">
                              {campaign.url}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">{campaign.source}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{campaign.medium}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">
                          {campaign.clicks}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 text-right">
                          {campaign.signups}
                        </td>
                        <td className="py-3 px-4 text-sm text-right">
                          <div className="text-gray-900 font-medium">{campaign.conversions}</div>
                          <div className="text-xs text-gray-500">{conversionRate}%</div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">
                          ${campaign.revenue.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleCopyUrl(campaign.url, campaign.id)}
                              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Copy URL"
                            >
                              {copiedId === campaign.id ? (
                                <CheckIcon className="w-5 h-5 text-green-600" />
                              ) : (
                                <DocumentDuplicateIcon className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteCampaign(campaign.id)}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Create Campaign Link</h2>
              <p className="text-sm text-gray-600 mt-1">
                Generate a trackable URL with UTM parameters
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base URL
                </label>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="https://yoursite.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Name * (Internal)
                </label>
                <input
                  type="text"
                  value={newCampaign.name || ''}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="Summer Sale 2024"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source * (utm_source)
                  </label>
                  <input
                    type="text"
                    value={newCampaign.source || ''}
                    onChange={(e) => setNewCampaign({ ...newCampaign, source: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                    placeholder="google, twitter, newsletter"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medium * (utm_medium)
                  </label>
                  <input
                    type="text"
                    value={newCampaign.medium || ''}
                    onChange={(e) => setNewCampaign({ ...newCampaign, medium: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                    placeholder="cpc, social, email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign * (utm_campaign)
                </label>
                <input
                  type="text"
                  value={newCampaign.campaign || ''}
                  onChange={(e) => setNewCampaign({ ...newCampaign, campaign: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="summer_sale_2024"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content (utm_content) <span className="text-gray-400">Optional</span>
                  </label>
                  <input
                    type="text"
                    value={newCampaign.content || ''}
                    onChange={(e) => setNewCampaign({ ...newCampaign, content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                    placeholder="banner, sidebar"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term (utm_term) <span className="text-gray-400">Optional</span>
                  </label>
                  <input
                    type="text"
                    value={newCampaign.term || ''}
                    onChange={(e) => setNewCampaign({ ...newCampaign, term: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                    placeholder="keywords"
                  />
                </div>
              </div>

              {/* Preview URL */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview URL
                </label>
                <div className="text-sm text-gray-900 font-mono break-all">
                  {buildUTMUrl(newCampaign)}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCampaign({});
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                className="px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Create Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UTMTrackingPage;
